import { useAuth } from "../../providers/AuthProvider";
import React, { useEffect, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import {
  Alert,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { FormLabel } from "../../components/form-label/FormLabel";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  displayNameSchema,
  endpoint,
  partitaIvaSchema,
  required_error,
} from "../../services/shared";
import { Org } from "../../types";
import axios, {AxiosError} from "axios";
import EnhancedOrgTable from "../../components/table-rules/OrganizationsDenseTable";
import { collection, onSnapshot } from "@firebase/firestore";
import { db } from "../../services/firebase";
import {apiClient} from "../../services/api";

type FormData = z.infer<typeof formSchema>;
const formSchema = z.object({
  ragioneSociale: z.string({ required_error }).min(3),
  vatNumber: partitaIvaSchema,
  active: z.boolean({ required_error }),
  firstName: z.string({ required_error }).min(3),
  lastName: z.string({ required_error }).min(3),
  displayName: displayNameSchema,
});
const defaultValues = {
  ragioneSociale: undefined,
  vatNumber: undefined,
  active: undefined,
  firstName: undefined,
  lastName: undefined,
  displayName: undefined,
};
export default function OrganizationDetails() {
  const [method, setMethod] = useState<"create" | "update">("create");
  const [selected, setSelected] = useState<Org>();
  const [loading, setLoading] = useState<boolean>(true);
  const [orgs, setOrganizations] = useState<any[]>([]);
  const { user, refreshUser } = useAuth();
  const [snackState, setSnackState] = useState<boolean>(false);
  const [snackMessage, setSnackMessage] = useState<{
    severity: any;
    message: string;
  }>({
    severity: "success",
    message: "",
  });
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = form;
  useEffect(() => {
    const colRef = collection(db, "organizations");
    const unsubscribe = onSnapshot(
      colRef,
      (snap) => {
        const results: any[] = snap.docs.map((doc) => {
          return { ...doc.data(), uid: doc.id };
        }) as any[];
        setOrganizations(results);
        setLoading(false);
      },
      (error) => {
        console.error("Errore nella snapshot:", error);
        setLoading(false);
      },
    );
    return () => unsubscribe();
  }, []);
  const onSubmit = handleSubmit(async (data: any | Org) => {
    setSnackState(false);
    if (!user || !user.uid) {
      setSnackMessage({
        severity: "error",
        message: "User non è autenticato",
      });
      setSnackState(true);
      return;
    }
    let res = null;
    if (method === "update") data = { ...selected, ...data };
    const payload: Org = {
      ragioneSociale: data.ragioneSociale.toString().trim().toLowerCase(),
      displayName: data.displayName.toString().trim().toLowerCase(),
      vatNumber: data.vatNumber.toString().trim().toLowerCase(),
      firstName: data.firstName.toString().trim().toLowerCase(),
      lastName: data.lastName.toString().trim().toLowerCase(),
      active: data.active,
    };
    if (!!selected) payload.uid = data.uid;
    console.log({
      selected,
      payload,
      data,
      method
    })
    try {
      res = await apiClient.post(
          method === "create" ?
          `/v1/org/${method}` :
          `/v1/org/${method}/${payload.uid}`
          , payload, {
        headers: {
          "Content-Type": `application/json`,
        },
      });
    } catch (e: AxiosError | any) {
      console.error("errore", e);
      setSnackMessage({
        severity: "error",
        message: e.response.data.error,
      });
      setSnackState(true);
    }
    if (res?.data.ok) {
      setSnackMessage({ severity: "success", message: method === "create" ? "Organizzazione creata" : "Aggiornamento eseguito" });
      setSnackState(true);
      await refreshUser();
      reset({
        ragioneSociale: payload.ragioneSociale,
        displayName: payload.displayName,
        vatNumber: payload.vatNumber,
        firstName: payload.firstName,
        lastName: payload.lastName,
        active: payload.active,
      });
    }
  });
  // ascolto le variazioni del method
  useEffect(() => {
    if (method === "update" && selected) {
      reset(selected);
    } else {
      reset(defaultValues);
    }
  }, [method, selected, reset]);
  const selectHandleSelection = (data: Org) => {
    setSelected(data);
    setMethod("update");
  };
  return (
    <div className="container">
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackState}
        onClose={() => setSnackState(false)}
        autoHideDuration={3000}
      >
        <Alert
          onClose={() => setSnackState(false)}
          severity={snackMessage.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackMessage.message}
        </Alert>
      </Snackbar>
      <FormLabel label={"Crea organizzazione"}></FormLabel>
      <form
        onSubmit={onSubmit}
        style={{
          padding: "1.25em",
          margin: "auto",
          marginBottom: "1em",
          maxWidth: "100%",
          background: "white",
          border: "1px solid #d1d1d1",
          borderRadius: ".5em",
        }}
        className="flex-col gap-2"
      >
        <FormLabel label={"Informazioni generali"}></FormLabel>
        <div className={"flex-row gap-2"} style={{ alignItems: "center" }}>
          <Controller
            name="ragioneSociale"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label="Ragione Sociale"
                placeholder="Es. Affida SRL"
                value={field.value ?? ""}
                error={!!errors.ragioneSociale}
                helperText={errors.ragioneSociale?.message}
              />
            )}
          />
          <Controller
            name="displayName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label="Nome visualizzato"
                placeholder="Es. Affita Credito Semplice"
                value={field.value ?? ""}
                error={!!errors.displayName}
                helperText={errors.displayName?.message}
              />
            )}
          />
        </div>
        <div className={"flex-row gap-2"} style={{ alignItems: "center" }}>
          <Controller
            name="vatNumber"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label="Parita IVA"
                placeholder="Es. 11239001008"
                value={field.value ?? ""}
                error={!!errors.vatNumber}
                helperText={errors.vatNumber?.message}
              />
            )}
          />
          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <div style={{ width: "100%", maxWidth: "225px" }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="active">Stato</InputLabel>
                  <Select
                    {...field}
                    style={{ textAlign: "left" }}
                    labelId="active"
                    value={typeof field.value === "boolean" ? field.value : ""}
                    onChange={(e) => field.onChange(e.target.value === "true")}
                    error={!!errors.active}
                  >
                    <MenuItem value="" disabled>
                      Seleziona una opzione
                    </MenuItem>
                    <MenuItem value="true">Attiva</MenuItem>
                    <MenuItem value="false">Non attiva</MenuItem>
                  </Select>
                  {errors.active && (
                    <FormHelperText style={{ color: "#d32f2f" }}>
                      {errors.active.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </div>
            )}
          />
        </div>
        <FormLabel label={"Legale rappresentante"}></FormLabel>
        <div className={"flex-row gap-2"} style={{ alignItems: "center" }}>
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label="Nome"
                placeholder="Es. Massimo"
                value={field.value ?? ""}
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
              />
            )}
          />
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label="Cognome"
                placeholder="Es. Rossi"
                value={field.value ?? ""}
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
              />
            )}
          />
        </div>
        <div className={"flex-row gap-2"} style={{ alignItems: "flex-end" }}>
          {!selected ? (
              <Button variant={"contained"} type={"submit"} disabled={isSubmitting}>
                {isSubmitting ? "Elaboro richiesta" : "Crea"}
              </Button>
          ) : (
              <div className="flex-row gap-2">
                <Button
                    variant={"contained"}
                    type={"submit"}
                    disabled={isSubmitting}
                >
                  {isSubmitting ? "Elaboro richiesta" : "Modifica"}
                </Button>
                <Button
                    variant={"outlined"}
                    type={"button"}
                    disabled={isSubmitting}
                    onClick={() => setSelected(undefined)}
                >
                  {isSubmitting ? "Elaboro richiesta" : "Annulla modifiche"}
                </Button>
              </div>
          )}
        </div>
      </form>
      <div>
        <FormLabel label={"Lista organizzazioni"}></FormLabel>
        {loading ? (
          <p>Caricamento...</p>
        ) : (
          <EnhancedOrgTable data={orgs} onSelect={selectHandleSelection} />
        )}
      </div>
    </div>
  );
}
